import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { setAccountId, setLatitude, setLongitude, setSubmitted, setBoundary, setSource } from '../../store/features/map2Slice'
import { useGetMap2BoundaryMutation } from '../../store/services/map2service'
import {
  Grid,
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material'

const Map2Form = () => {
  const dispatch = useDispatch()
  const { accountId, latitude, longitude, submitted } = useSelector((state: RootState) => state.map2)
  const [getMap2Boundary, { error, isLoading }] = useGetMap2BoundaryMutation()
  const [showToast, setShowToast] = React.useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (accountId && latitude && longitude) {
      try {
        const result = await getMap2Boundary({
          account_id: accountId,
          lat: parseFloat(latitude),
          lon: parseFloat(longitude)
        }).unwrap()

        if (!result.features || !result.features[0]?.geometry?.coordinates) {
          setShowToast(true)
          return
        }

        // Handle both single and multiple polygons
        const processPolygon = (coordinates: number[][][]) => {
          return coordinates.map(ring => 
            ring.map(coord => ({
              lat: coord[1],
              lng: coord[0]
            }))
          );
        };

        // Handle both single and multiple features
        const allPolygons = result.features.flatMap(feature => {
          if (feature.geometry.type === 'Polygon') {
            const coords = feature.geometry.coordinates as number[][][];
            return [processPolygon(coords)];
          } else if (feature.geometry.type === 'MultiPolygon') {
            const coords = feature.geometry.coordinates as number[][][][];
            return coords.map(polygonCoords => processPolygon(polygonCoords));
          }
          return [];
        });

        dispatch(setSource(result?.source || null))
        dispatch(setBoundary(allPolygons))
        dispatch(setSubmitted(true))
      } catch (err) {
        console.error('Failed to get boundary:', err)
      }
    }
  }

  const handleReset = () => {
    dispatch(setAccountId(''))
    dispatch(setLatitude(''))
    dispatch(setLongitude(''))
    dispatch(setSubmitted(false))
    dispatch(setBoundary([]))
  }

  const handleCloseToast = () => {
    setShowToast(false)
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Enter Location Details
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to get boundary data. Please try again.
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Account ID"
                value={accountId}
                onChange={(e) => dispatch(setAccountId(e.target.value))}
                required
              // disabled={submitted}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Latitude"
                value={latitude}
                onChange={(e) => dispatch(setLatitude(e.target.value))}
                required
                type="number"
                inputProps={{
                  step: "any",
                  min: -90,
                  max: 90
                }}
              // disabled={submitted}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Longitude"
                value={longitude}
                onChange={(e) => dispatch(setLongitude(e.target.value))}
                required
                type="number"
                inputProps={{
                  step: "any",
                  min: -180,
                  max: 180
                }}
              // disabled={submitted}
              />
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Submit'
                    )}
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    onClick={handleReset}
                    variant="outlined"
                    color="primary"
                    fullWidth
                    size="large"
                  >
                    Reset
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </Paper>
      <Snackbar
        open={showToast}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        message="No boundary data found for the given location"
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  )
}

export default Map2Form 